// Updated Signup.js with Django API integration

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, handleAPIError } from '../services/api';
import { Loader, AlertCircle, Eye, EyeOff, CheckCircle } from 'lucide-react';
import '../css/loginSignup.css';
import logoBlack from '../Images/Home/logo_black.png';

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Check if user is already authenticated
  useEffect(() => {
    if (authAPI.isAuthenticated()) {
      navigate('/dashboard2');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleFocus = (field) => {
    console.log(`Focused: ${field}`);
    setFocusedField(field);
  };

  const handleBlur = () => {
    console.log(`Blurred: ${focusedField}`);
    setFocusedField(null);
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    
    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.password.trim()) {
      setError('Password is required');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const userData = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password
      };
      
      console.log('Signing up user:', userData);
      
      // Call Django API for signup
      const response = await authAPI.signup(userData);
      
      console.log('Signup successful:', response);
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Signup failed:', error);
      setError(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Show success message
  if (success) {
    return (
      <div className='lsMain'>
        <div className="loginSignup success-container">
          <Link to={'/'}>
            <img src={logoBlack} alt="rf logo"/>
          </Link>
          <div className="success-message">
            <CheckCircle size={48} className="text-green-500" />
            <h2>Account Created Successfully!</h2>
            <p>Welcome to Red Fibre! Your account has been created.</p>
            <p>Redirecting to login page...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='lsMain'>
      <form className="loginSignup" onSubmit={handleSubmit}>
        <Link to={'/'}>
          <img src={logoBlack} alt="rf logo"/>
        </Link>
        <h1>SIGNUP</h1>
        
        {error && (
          <div className="error-message" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#e74c3c',
            background: '#fdf2f2',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
            marginBottom: '20px'
          }}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}
        
        <div className="lp">
          <div className={`lsep ${focusedField === 'username' ? 'focused' : ''}`}>
            <input
              type="text"
              name="username"
              placeholder='Username'
              value={formData.username}
              onChange={handleChange}
              onFocus={() => handleFocus('username')}
              onBlur={handleBlur}
              required
              disabled={loading}
              autoComplete="username"
            />
          </div>
          
          <div className={`lsep ${focusedField === 'email' ? 'focused' : ''}`}>
            <input
              type="email"
              name="email"
              placeholder='Email'
              value={formData.email}
              onChange={handleChange}
              onFocus={() => handleFocus('email')}
              onBlur={handleBlur}
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>
          
          <div className={`lsep ${focusedField === 'password' ? 'focused' : ''}`}>
            <div className="password-input-wrapper" style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder='Password'
                value={formData.password}
                onChange={handleChange}
                onFocus={() => handleFocus('password')}
                onBlur={handleBlur}
                required
                disabled={loading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={loading}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          
          <div className={`lsep ${focusedField === 'confirmPassword' ? 'focused' : ''}`}>
            <input
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder='Confirm Password'
              value={formData.confirmPassword}
              onChange={handleChange}
              onFocus={() => handleFocus('confirmPassword')}
              onBlur={handleBlur}
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
          
          <button type="submit" disabled={loading}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Loader className="animate-spin" size={16} />
                Creating Account...
              </span>
            ) : (
              'Signup'
            )}
          </button>
        </div>
        
        <p>Already have an account? <Link to={'/login'}>Login</Link></p>
      </form>
    </div>
  );
};

export default Signup;